export interface OptimizationRequest {
  planId: string;
  paramId: number;
  outputUrl?: string;
  metadata?: string;
}

export interface OptimizationState {
  isRunning: boolean;
  planId?: string;
  lastResult?: OptimizationResult;
}

export interface OptimizationResult {
  planId: string;
  status: JobStatusV4;
  jobId?: string;
  outputPlanId?: string;
  /**
   * The job's cancelability at the status being reported, so a row's verdict is refreshed by the
   * same event that moves its status. Socket-only on the wire; absence reads as "no".
   */
  isCancelable?: boolean;
  dataSpaceId?: number;
  planningSpaceId?: string;
  canceledBy?: { id: number; fullname: string };
}

export enum JobStatusV4 {
  CREATED = 'CREATED',
  SENT = 'SENT',
  RUNNING = 'RUNNING',
  ERROR = 'ERROR',
  RETRY = 'RETRY',
  OPTIMIZATION_FINISHED = 'OPTIMIZATION_FINISHED',
  BLOCKED = 'BLOCKED',
  CANCELED = 'CANCELED',
  UNKNOWN = 'UNKNOWN'
}

/** DataLayer's classification of a job, which decides how it can be stopped. */
export enum JobSize {
  SMALL = 'SMALL',
  LARGE = 'LARGE'
}

/**
 * `details` of a 409 `optimization_not_cancelable`. Its keys stay snake_case: GBE
 * snake_cases exception bodies, and `apiResponseCaseInterceptor` leaves `details` verbatim
 * (see `CODED_MESSAGE_VERBATIM_KEYS`). `job_size` is null when the large-job feature flag
 * is off, so the job was never classified.
 */
export interface NotCancelableDetails {
  status?: JobStatusV4;
  job_size?: JobSize | null;
}

/**
 * The subset of `GET/POST v4/optimizations/{id}` we consume. The response interceptor
 * camelCases the wire's `output_plan`, which stays populated on a canceled run that
 * finished in the window between the cancel and the pod delete.
 */
export interface OptimizationResponse {
  id: string;
  status: JobStatusV4;
  plan: { id: string };
  outputPlan: { id: string } | null;
  isCancelable?: boolean;
}

export class JobStatusUtils {
  static isUnfinishedStatus(status: JobStatusV4): boolean {
    return (
      status === JobStatusV4.CREATED ||
      status === JobStatusV4.SENT ||
      status === JobStatusV4.RUNNING ||
      status === JobStatusV4.RETRY
    );
  }
  static isStatusFinished(status: JobStatusV4): boolean {
    return status === JobStatusV4.OPTIMIZATION_FINISHED;
  }
  static isStatusRunning(status: JobStatusV4): boolean {
    return status === JobStatusV4.RUNNING;
  }
  static isStatusSent(status: JobStatusV4): boolean {
    return status === JobStatusV4.SENT;
  }
  static isErrorStatus(status: JobStatusV4): boolean {
    return status === JobStatusV4.ERROR;
  }
  /**
   * A run DataLayer refused because the plan holds a blocking finding. Terminal and
   * deterministic, unlike ERROR — retrying the same plan can only be refused again.
   */
  static isBlockedStatus(status: JobStatusV4): boolean {
    return status === JobStatusV4.BLOCKED;
  }

  /**
   * The run is over. CANCELED belongs here even though a canceled job may still move to
   * OPTIMIZATION_FINISHED — that transition is pushed over the websocket, so nothing has
   * to keep treating the job as live to catch it.
   *
   * Deliberately not the negation of `isUnfinishedStatus`, which counts RETRY as unfinished:
   * UNKNOWN is neither unfinished nor terminal.
   */
  static isTerminalStatus(status: JobStatusV4): boolean {
    return (
      status === JobStatusV4.OPTIMIZATION_FINISHED ||
      status === JobStatusV4.ERROR ||
      status === JobStatusV4.BLOCKED ||
      status === JobStatusV4.CANCELED
    );
  }

  /**
   * The run is over and left nothing behind, so retiring it loses no result. Narrower than
   * `isTerminalStatus`: a finished run owns its output plan, and BLOCKED is a verdict on the
   * plan rather than a failed attempt — the back end refuses both.
   */
  static isDeletableStatus(status: JobStatusV4): boolean {
    return status === JobStatusV4.ERROR || status === JobStatusV4.CANCELED;
  }
}
